import { useEffect, useRef } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { QUERY_CACHE_KEY } from "@/lib/perf";

/**
 * Cache safety: when the authenticated user changes (sign in, sign out,
 * account switch), wipe React Query cache + persisted localStorage cache
 * + any per-user UI state. Prevents one user from briefly seeing another
 * user's cached content on shared devices.
 */
export function CacheGuard() {
  const queryClient = useQueryClient();
  const lastUserId = useRef<string | null | undefined>(undefined);

  useEffect(() => {
    const wipe = () => {
      try {
        queryClient.clear();
        localStorage.removeItem(QUERY_CACHE_KEY);
        localStorage.removeItem("jozori_active_child");
      } catch {
        /* private mode / quota — ignore */
      }
    };

    supabase.auth.getSession().then(({ data }) => {
      lastUserId.current = data.session?.user?.id ?? null;
    });

    const { data: sub } = supabase.auth.onAuthStateChange((event, session) => {
      const nextId = session?.user?.id ?? null;
      if (event === "SIGNED_OUT") {
        wipe();
        lastUserId.current = null;
        return;
      }
      if (
        event === "SIGNED_IN" &&
        lastUserId.current !== undefined &&
        lastUserId.current !== nextId
      ) {
        // Different user signed in — clear the previous user's footprint.
        wipe();
      }
      lastUserId.current = nextId;
    });

    return () => sub.subscription.unsubscribe();
  }, [queryClient]);

  return null;
}
